import { cloneDeep, last } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { MESSAGE_EVENT, MESSAGE_TYPE } from '../constants/communication.constant';
import { JOURNEY_POSTBACK_TYPE, JOURNEY_STEP_TYPE } from '../constants/journey.constant';
import { MessagingMessage } from '../models';
import {
  JourneyStepAction,
  JourneyStepActionMessage,
  JourneyStepActionPostback,
  JourneyStepActionPostbackHttps,
  JourneyStepActionPostbackIntegrationAdobe,
  JourneyStepAny,
  JourneyStepEnd,
  JourneyStepFlowEvent,
  JourneyStepFlowInteraction,
  JourneyStepFlowJoin,
  JourneyStepFlowRandom,
  JourneyStepFlowSplit,
  JourneyStepFlowWait,
  JourneyStepFlowWaitInteraction,
  JourneyStepId,
  JourneyStepPath,
  JourneyStepSourceAudience,
  JourneyStepSourceEvents,
} from '../models/journey.model';
import { getDefaultSegment, isValidFilter } from './filters.helper';
import {
  hasNext,
  hasPaths,
  isActionMessageStep,
  isActionPostbackHttpsStep,
  isActionPostbackIntegrationAdobeStep,
  isActionStep,
  isSourceAudienceStep,
} from './journey.guard';

const ONE_DAY_IN_SECONDS = 24 * 60 * 60;

/**
 * Returns a new UUIDv4
 */
export const getNewStepId = (): JourneyStepId => uuidv4();

/**
 * Returns a blank step
 * @param type Step type to return
 */
export function getDefaultStep(
  type: JOURNEY_STEP_TYPE.ACTION_MESSAGE_EMAIL,
  next?: string
): JourneyStepAction;
export function getDefaultStep(
  type: JOURNEY_STEP_TYPE.ACTION_MESSAGE_IN_APP,
  next?: string
): JourneyStepAction;
export function getDefaultStep(
  type: JOURNEY_STEP_TYPE.ACTION_MESSAGE_PUSH,
  next?: string
): JourneyStepActionMessage;
export function getDefaultStep(
  type: JOURNEY_STEP_TYPE.ACTION_POSTBACK,
  next?: string
): JourneyStepActionPostback;
export function getDefaultStep(type: JOURNEY_STEP_TYPE.END, next?: string): JourneyStepEnd;
export function getDefaultStep(type: JOURNEY_STEP_TYPE.FLOW_EVENT, next?: string): JourneyStepFlowEvent;
export function getDefaultStep(
  type: JOURNEY_STEP_TYPE.FLOW_INTERACTION,
  next?: string
): JourneyStepFlowInteraction;
export function getDefaultStep(
  type: JOURNEY_STEP_TYPE.FLOW_WAIT_INTERACTION,
  next?: string
): JourneyStepFlowWaitInteraction;
export function getDefaultStep(type: JOURNEY_STEP_TYPE.FLOW_JOIN, next?: string): JourneyStepFlowJoin;
export function getDefaultStep(type: JOURNEY_STEP_TYPE.FLOW_RANDOM, next?: string): JourneyStepFlowRandom;
export function getDefaultStep(type: JOURNEY_STEP_TYPE.FLOW_SPLIT, next?: string): JourneyStepFlowSplit;
export function getDefaultStep(type: JOURNEY_STEP_TYPE.FLOW_WAIT, next?: string): JourneyStepFlowWait;
export function getDefaultStep(
  type: JOURNEY_STEP_TYPE.SOURCE_AUDIENCE,
  next?: string
): JourneyStepSourceAudience;
export function getDefaultStep(type: JOURNEY_STEP_TYPE.SOURCE_EVENTS, next?: string): JourneyStepSourceEvents;
export function getDefaultStep(type: JOURNEY_STEP_TYPE, next?: string): JourneyStepAny;
export function getDefaultStep(type: JOURNEY_STEP_TYPE, next = ''): JourneyStepAny {
  const id = getNewStepId();
  if (
    type === JOURNEY_STEP_TYPE.FLOW_INTERACTION ||
    type === JOURNEY_STEP_TYPE.FLOW_EVENT ||
    type === JOURNEY_STEP_TYPE.FLOW_SPLIT
  ) {
    let firstPath: JourneyStepPath | undefined;
    if (next) {
      switch (type) {
        case JOURNEY_STEP_TYPE.FLOW_INTERACTION:
          firstPath = { name: '', next, action: '' };
          break;
        case JOURNEY_STEP_TYPE.FLOW_EVENT:
          firstPath = { name: '', next, eventId: undefined };
          break;
        case JOURNEY_STEP_TYPE.FLOW_SPLIT:
          firstPath = { name: '', next, segment: getDefaultSegment() };
          break;
      }
      return {
        id,
        type,
        paths: [firstPath, { name: '', next: '' }],
      };
    } else {
      return { id, type, paths: [] };
    }
  } else if (type === JOURNEY_STEP_TYPE.FLOW_WAIT_INTERACTION) {
    return {
      id,
      type,
      paths: [
        { name: '', next, action: '' },
        {
          name: '',
          next: '',
          duration: ONE_DAY_IN_SECONDS,
        },
      ],
    };
  } else if (type === JOURNEY_STEP_TYPE.FLOW_RANDOM) {
    return {
      id,
      type,
      paths: [
        { name: '', amount: 0.5, next },
        { name: '', amount: 0.5, next: '' },
      ],
    };
  } else if (type === JOURNEY_STEP_TYPE.END) {
    return { id, type };
  } else if (type === JOURNEY_STEP_TYPE.FLOW_WAIT) {
    return { id, duration: ONE_DAY_IN_SECONDS, type, next };
  } else if (type === JOURNEY_STEP_TYPE.SOURCE_EVENTS) {
    return { id, eventIds: [], type, next };
  } else if (type === JOURNEY_STEP_TYPE.SOURCE_AUDIENCE) {
    return { id, type, audienceId: 0, next };
  } else if (type === JOURNEY_STEP_TYPE.ACTION_POSTBACK) {
    return {
      id,
      type,
      postbackType: JOURNEY_POSTBACK_TYPE.HTTPS,
      next,
    };
  } else {
    return { id, type, next } as JourneyStepAny;
  }
}

/**
 * Returns a blank Postback step
 * @param postbackType Postback type to return
 */
export function getDefaultPostbackStep(
  postbackType: JOURNEY_POSTBACK_TYPE.HTTPS,
  next?: string
): JourneyStepActionPostbackHttps;
export function getDefaultPostbackStep(
  postbackType: JOURNEY_POSTBACK_TYPE.INTEGRATION_ADOBE,
  next?: string
): JourneyStepActionPostbackIntegrationAdobe;
export function getDefaultPostbackStep(
  postbackType: JOURNEY_POSTBACK_TYPE,
  next?: string
): JourneyStepActionPostback;
export function getDefaultPostbackStep(
  postbackType: JOURNEY_POSTBACK_TYPE,
  next = ''
): JourneyStepActionPostback {
  const id = getNewStepId();
  if (postbackType === JOURNEY_POSTBACK_TYPE.HTTPS) {
    return {
      id,
      type: JOURNEY_STEP_TYPE.ACTION_POSTBACK,
      postbackType,
      postbackUrl: '',
      postbackBody: [],
      postbackHeader: [],
      postbackMethod: 'POST',
      next,
    } as JourneyStepActionPostbackHttps;
  } else if (postbackType === JOURNEY_POSTBACK_TYPE.INTEGRATION_ADOBE) {
    return {
      id,
      type: JOURNEY_STEP_TYPE.ACTION_POSTBACK,
      postbackType,
      integrationId: 0,
      postbackBody: [],
      next,
    } as JourneyStepActionPostbackIntegrationAdobe;
  } else {
    throw new Error('Invalid postback type');
  }
}

/**
 * Gets every step pointing to a step by its ID
 * @param steps Journey steps
 * @param next Step ID
 */
export const getStepsByNext = (steps: JourneyStepAny[], next: JourneyStepId): JourneyStepAny[] =>
  steps.filter((step) => {
    if (hasPaths(step)) {
      return step.paths.some((path: JourneyStepPath) => path.next === next);
    } else {
      return step.next === next;
    }
  });

/**
 * Gets a step by its ID
 * @param steps Journey steps
 * @param id Step ID
 */
export const getStepById = (steps: JourneyStepAny[], id: JourneyStepId): JourneyStepAny | undefined =>
  steps.find((s) => s.id === id);

/**
 * Gets every previous direct action
 * @param steps Journey steps
 * @param stepId Step ID to start searching
 */
export const getPreviousActions = (steps: JourneyStepAny[], stepId: JourneyStepId): JourneyStepAction[] => {
  const queue: JourneyStepId[] = [stepId];
  const actions: JourneyStepAction[] = [];
  do {
    const id: JourneyStepId = queue.pop() as JourneyStepId;
    const current = getStepById(steps, id);
    if (current) {
      if (isActionStep(current)) {
        actions.push(current);
      } else {
        const prev = getStepsByNext(steps, id);
        if (prev.length) {
          queue.push(...prev.map((s) => s.id));
        }
      }
    }
  } while (queue.length);
  return actions;
};

/**
 * Gets every previous direct message actions
 * @param steps Journey steps
 * @param stepId Step ID to start searching
 */
export const getPreviousActionMessages = (
  steps: JourneyStepAny[],
  stepId: JourneyStepId
): JourneyStepActionMessage[] => {
  const queue: JourneyStepId[] = [stepId];
  const actions: JourneyStepActionMessage[] = [];
  do {
    const id: JourneyStepId = queue.pop() as JourneyStepId;
    const current = getStepById(steps, id);
    if (current) {
      if (isActionMessageStep(current)) {
        actions.push(current);
      } else {
        const prev = getStepsByNext(steps, id);
        if (prev.length) {
          queue.push(...prev.map((s) => s.id));
        }
      }
    }
  } while (queue.length);
  return actions;
};

/**
 * Gets all the steps after a given step
 * @param steps Journey steps
 * @param stepId Step ID to start searching
 */
export const getNextSteps = (steps: JourneyStepAny[], stepId: JourneyStepId): JourneyStepAny[] => {
  const queue: JourneyStepId[] = [stepId];
  const nextSteps: JourneyStepAny[] = [];
  do {
    const id: JourneyStepId = queue.pop() as JourneyStepId;
    const current = getStepById(steps, id);
    if (current) {
      if (current.id !== stepId) {
        nextSteps.push(current);
      }
      // Fill the queue with the next steps
      if (hasNext(current)) {
        const next = getStepById(steps, current.next);
        if (next) {
          queue.push(next.id);
        }
      } else if (hasPaths(current)) {
        const paths = current.paths;
        if (paths.length) {
          queue.push(...paths.map((s) => s.next));
        }
      }
    }
  } while (queue.length);
  return nextSteps;
};

/**
 * Inserts a step before another step
 * @param steps Journey steps to modify in place
 * @param step Step to insert
 * @param prependTo Id of the step to prepend this step
 */
export const insertStep = (
  steps: JourneyStepAny[],
  step: JourneyStepAny,
  prependTo?: JourneyStepId
): JourneyStepAny[] => {
  if (prependTo === undefined) {
    prependTo = step.next;
  }
  if (prependTo) {
    const prevSteps = getStepsByNext(steps, prependTo);
    for (const prev of prevSteps) {
      _replaceNext(prev, step.id, prependTo);
    }
    const lastPrev = last(prevSteps);
    _pushStep(steps, step, lastPrev ? steps.indexOf(lastPrev) : -1);
  } else {
    console.error({ step, currentNext: prependTo });
    throw new Error('No next step found inserting step');
  }
  addEndings(steps);
  return steps;
};

/**
 * Replaces a step with another step
 * @param steps Journey steps to modify in place
 * @param step Replacement
 * @param currentId Id of the current step to be replaced
 */
export const replaceStep = (
  steps: JourneyStepAny[],
  step: JourneyStepAny,
  currentId: JourneyStepId
): JourneyStepAny[] => {
  const idx = steps.findIndex((s) => s.id === currentId);
  const prevSteps = getStepsByNext(steps, currentId);
  for (const prev of prevSteps) {
    _replaceNext(prev, step.id, currentId);
  }
  if (idx >= 0) {
    steps.splice(idx, 1, step);
  } else {
    console.error({ step, currentId });
    throw new Error('No original step found replacing step');
  }
  addEndings(steps);
  return steps;
};

/**
 * Removes the step and all its children
 * @param steps Journey steps to modify in place
 * @param step Step to be removed along with its children
 */
export const _removeStepBranch = (steps: JourneyStepAny[], step: JourneyStepAny): JourneyStepAny[] => {
  const nexts: string[] = [];
  if (hasPaths(step)) {
    const paths: JourneyStepPath[] = step.paths;
    nexts.push(...paths.map((s) => s.next));
  } else if (step.next) {
    nexts.push(step.next);
  }
  nexts.forEach((n) => {
    const next = steps.find((s) => s.id === n);
    if (next) {
      _removeStepBranch(steps, next);
    }
  });
  const idx = steps.findIndex((s) => s.id === step.id);
  if (idx >= 0) {
    steps.splice(idx, 1);
  }
  return steps;
};

/**
 * Removes a single step, discarding any parentless children
 * @param steps Journey steps to modify in place
 * @param step Step to be removed along with any parentless children
 */
export const removeStep = (steps: JourneyStepAny[], step: JourneyStepAny): JourneyStepAny[] => {
  if (hasPaths(step)) {
    _removeStepBranch(steps, step);
    for (const prevStep of getStepsByNext(steps, step.id)) {
      _replaceNext(prevStep, '', step.id);
    }
  } else {
    if (step.next !== undefined) {
      for (const prevStep of getStepsByNext(steps, step.id)) {
        _replaceNext(prevStep, step.next, step.id);
      }
    } else if (step.type !== JOURNEY_STEP_TYPE.END) {
      console.error({ step });
      throw new Error('Unable to remove step without next');
    }
  }
  const idx = steps.findIndex((s) => s.id === step.id);
  if (idx >= 0) {
    steps.splice(idx, 1);
  }
  addEndings(steps);
  return steps;
};

/**
 * Moves a single step
 * @param steps Journey steps to modify in place
 * @param step Step to move
 * @param prependTo Id of the destination step to prepend this step
 */
export const moveStep = (
  steps: JourneyStepAny[],
  step: JourneyStepAny,
  prependTo: JourneyStepId
): JourneyStepAny[] => {
  if (step.id !== prependTo) {
    if (hasNext(step)) {
      if (step.next) {
        // Change origin and distination nexts
        const prevStepsOrig = getStepsByNext(steps, step.id);
        const prevStepsDest = getStepsByNext(steps, prependTo);
        for (const prevStep of prevStepsOrig) {
          _replaceNext(prevStep, step.next, step.id);
        }
        for (const prevStep of prevStepsDest) {
          _replaceNext(prevStep, step.id, prependTo);
        }
        // Change step next
        _replaceNext(step, prependTo, step.next);
        // If there is a better place, move it
        const lastPrevDest = last(prevStepsDest);
        if (lastPrevDest) {
          const idx = steps.findIndex((s) => s.id === step.id);
          const destIdx = steps.indexOf(lastPrevDest) + 1;
          if (idx === -1 || destIdx !== idx) {
            // Remove from its current position
            if (idx >= 0) {
              steps.splice(idx, 1);
            }
            // Move to new position
            steps.splice(steps.indexOf(lastPrevDest) + 1, 0, step);
          }
        } else {
          console.warn('Moved a detached step', { step });
          steps.push(step);
        }
      } else {
        console.error({ step });
        throw new Error('Unable to move step without next');
      }
    } else {
      console.error({ step });
      throw new Error('Unable to move a multi-next step');
    }
  }
  addEndings(steps);
  return steps;
};

/**
 * (Internal) Replaces the step's next with another id
 * @param step Step to change next
 * @param next New next
 * @param currentNext Current next (required to unify with SPLIT and INTERACTION)
 */
export const _replaceNext = (
  step: JourneyStepAny,
  next: JourneyStepId,
  currentNext: JourneyStepId
): JourneyStepAny => {
  if (hasPaths(step)) {
    const paths: JourneyStepPath[] = step.paths;
    const path = paths.find((p) => p.next === currentNext);
    if (path) {
      Object.assign(path, { next });
    }
  } else if (step.next === currentNext) {
    return Object.assign(step, { next });
  }
  return step;
};

/**
 * Pushes a step into steps, at a location or at the end
 * @param steps Journey steps to modify in place
 * @param step Step to insert
 * @param idx Index (-1 to insert at the end)
 */
export const _pushStep = (steps: JourneyStepAny[], step: JourneyStepAny, idx: number): void => {
  if (idx >= 0) {
    steps.splice(idx + 1, 0, step);
  } else {
    steps.push(step);
  }
};

/**
 * Adds endings to every paths without end
 * @param steps Journey steps to modify in place
 */
export const addEndings = (steps: JourneyStepAny[]): void => {
  for (const step of steps) {
    if (hasNext(step)) {
      if (!step.next) {
        const end = getDefaultStep(JOURNEY_STEP_TYPE.END);
        step.next = end.id;
        const idx = steps.findIndex((s) => s.id === step.id);
        _pushStep(steps, end, idx);
      }
    } else if (hasPaths(step)) {
      const paths: JourneyStepPath[] = step.paths;
      for (const path of paths.filter((p) => !p.next)) {
        const end = getDefaultStep(JOURNEY_STEP_TYPE.END);
        Object.assign(path, { next: end.id });
        const idx = steps.findIndex((s) => s.id === step.id);
        _pushStep(steps, end, idx);
      }
    }
  }
};

/**
 * Changes a step's Id
 * @param steps Journey steps to modify in place
 * @param fromId Current Id
 * @param toId New Id
 */
export const changeStepId = (steps: JourneyStepAny[], fromId: JourneyStepId, toId?: JourneyStepId): void => {
  const step = getStepById(steps, fromId);
  if (step) {
    step.id = toId ?? getNewStepId();
    const parents = getStepsByNext(steps, fromId);
    for (const parent of parents) {
      if (hasPaths(parent)) {
        for (const path of parent.paths) {
          if (path.next === fromId) {
            path.next = step.id;
          }
        }
      } else {
        parent.next = step.id;
      }
    }
  }
};

/**
 * Check waits for every step that needs a previous wait
 * @param steps Journey steps
 */
export const _needWaitsBefore = (steps: JourneyStepAny[]): JourneyStepId[] => {
  const need: JourneyStepId[] = [];
  for (const step of steps.filter((s) =>
    [JOURNEY_STEP_TYPE.FLOW_INTERACTION, JOURNEY_STEP_TYPE.FLOW_EVENT].includes(s.type)
  )) {
    const prev = getStepsByNext(steps, step.id);
    if (
      !prev.length ||
      prev.some((p) => {
        // EN-2022. Sólo un path del FLOW_WAIT_INTERACTION tienen un wait
        if (hasPaths(p)) {
          const path = p.paths.find((path) => path.next === step.id);
          return p.type !== JOURNEY_STEP_TYPE.FLOW_WAIT_INTERACTION || !path?.duration;
        }
        return p.type !== JOURNEY_STEP_TYPE.FLOW_WAIT;
      })
    ) {
      need.push(step.id);
    }
  }
  return need;
};

/**
 * Check waits for every step that needs a wait after
 * @param steps Journey steps
 */
export const _needWaitsAfter = (steps: JourneyStepAny[]): JourneyStepId[] => {
  const need: JourneyStepId[] = [];
  for (const step of steps.filter((s) => [JOURNEY_STEP_TYPE.ACTION_MESSAGE_IN_APP].includes(s.type))) {
    const next = step.next && getStepById(steps, step.next);
    if (
      next &&
      next.type !== JOURNEY_STEP_TYPE.FLOW_WAIT &&
      next.type !== JOURNEY_STEP_TYPE.FLOW_WAIT_INTERACTION
    ) {
      need.push(next.id);
    }
  }
  return need;
};

/**
 * Check waits for every step that needs one
 * @param steps Journey steps
 */
export const needWaits = (steps: JourneyStepAny[]): JourneyStepId[] => {
  return ([] as JourneyStepId[]).concat(_needWaitsBefore(steps)).concat(_needWaitsAfter(steps));
};

/**
 * Check contiguous waits
 * @param steps Journey steps
 */
export const hasContiguousWaits = (steps: JourneyStepAny[]): JourneyStepId[] => {
  const need: JourneyStepId[] = [];
  for (const step of steps.filter((s) => s.type === JOURNEY_STEP_TYPE.FLOW_WAIT)) {
    if (hasNext(step)) {
      const next = getStepById(steps, step.next);
      if (next && next.type === JOURNEY_STEP_TYPE.FLOW_WAIT) {
        need.push(step.id);
      }
    }
  }
  return need;
};

/**
 * Adds a wait before every step without wait when needed
 * @param steps Journey steps to modify in place
 */
export const addWaits = (steps: JourneyStepAny[]): void => {
  for (const stepId of needWaits(steps)) {
    const wait = getDefaultStep(JOURNEY_STEP_TYPE.FLOW_WAIT);
    wait.next = stepId;
    insertStep(steps, wait);
  }
};

/**
 * Collapses consecutive wait-like to a single wait or end
 * @param steps Journey steps to modify in place
 */
export const collapseWaits = (steps: JourneyStepAny[]): void => {
  const waits = steps.filter((s) => s.type === JOURNEY_STEP_TYPE.FLOW_WAIT) as JourneyStepFlowWait[];
  for (const step of waits) {
    const next = getStepById(steps, step.next);
    if (next && next.type === JOURNEY_STEP_TYPE.FLOW_WAIT) {
      const duration = step.duration + next.duration;
      next.duration = duration;
      removeStep(steps, step);
    }
  }
};

/**
 * Checks if the step has everything set up
 * @param steps Journey steps to modify in place
 */
export const isValidStep = (step: JourneyStepAny): boolean => {
  if (
    step.type === JOURNEY_STEP_TYPE.ACTION_MESSAGE_PUSH ||
    step.type === JOURNEY_STEP_TYPE.ACTION_MESSAGE_EMAIL ||
    step.type === JOURNEY_STEP_TYPE.ACTION_MESSAGE_IN_APP
  ) {
    return Boolean(step.campaignId && step.messageId);
  } else if (step.type === JOURNEY_STEP_TYPE.ACTION_POSTBACK) {
    let isValidBody = true;
    if (isActionPostbackHttpsStep(step)) {
      try {
        if (typeof step.postbackBody === 'string') {
          JSON.parse(step.postbackBody);
        } else {
          isValidBody =
            Array.isArray(step.postbackBody) && step.postbackBody.every((p) => p.param && p.value);
        }
      } catch {
        isValidBody = false;
      }
    }
    return (
      (isActionPostbackHttpsStep(step) &&
        Boolean(step.postbackUrl) &&
        (step.postbackUrl.startsWith('http://') || step.postbackUrl.startsWith('https://')) &&
        isValidBody &&
        Array.isArray(step.postbackHeader)) ||
      (isActionPostbackIntegrationAdobeStep(step) &&
        Boolean(step.integrationId) &&
        Array.isArray(step.postbackBody) &&
        step.postbackBody.every((p) => p.param && p.value))
    );
  } else if (step.type === JOURNEY_STEP_TYPE.FLOW_EVENT) {
    const { paths } = step;
    return paths.length > 1 && !paths.slice(0, paths.length - 1).some((p) => !p.eventId);
  } else if (step.type === JOURNEY_STEP_TYPE.FLOW_SPLIT) {
    const { paths } = step;
    return (
      paths.length > 1 &&
      !paths
        .slice(0, paths.length - 1)
        .some((p) => !(p.segment?.config?.length && !p.segment.config.some((f) => !isValidFilter(f))))
    );
  } else if (step.type === JOURNEY_STEP_TYPE.FLOW_INTERACTION) {
    const { paths } = step;
    return paths.length > 1 && !paths.slice(0, paths.length - 1).some((p) => !p.action);
  } else if (step.type === JOURNEY_STEP_TYPE.FLOW_WAIT_INTERACTION) {
    const { paths } = step;
    return (
      paths.length > 1 &&
      !paths.slice(0, paths.length - 1).some((p) => !p.action) &&
      Boolean(paths[paths.length - 1].duration)
    );
  } else if (step.type === JOURNEY_STEP_TYPE.FLOW_RANDOM) {
    const { paths } = step;
    return paths.length > 1 && !paths.some((p) => !p.amount);
  } else if (step.type === JOURNEY_STEP_TYPE.FLOW_WAIT) {
    return Boolean(step.duration);
  } else if (step.type === JOURNEY_STEP_TYPE.SOURCE_EVENTS) {
    return Boolean(step.eventIds.length);
  } else if (step.type === JOURNEY_STEP_TYPE.SOURCE_AUDIENCE && 'segment' in step) {
    // condición para JourneyStepLegacyAudienceSource
    const filter = step.segment?.config?.[0];
    return Boolean(filter) && isValidFilter(filter);
  } else if (step.type === JOURNEY_STEP_TYPE.SOURCE_AUDIENCE) {
    return Boolean(step.audienceId);
  }
  return true;
};

/**
 * Checks if the action has a valid message
 * @param steps Journey steps to modify in place
 * @param messages Available messages
 */
export const isValidActionMessage = (step: JourneyStepAny, messages: MessagingMessage[]): boolean => {
  if (
    step.type === JOURNEY_STEP_TYPE.ACTION_MESSAGE_PUSH ||
    step.type === JOURNEY_STEP_TYPE.ACTION_MESSAGE_EMAIL ||
    step.type === JOURNEY_STEP_TYPE.ACTION_MESSAGE_IN_APP
  ) {
    return messages.some((m) => m.id === step.messageId && m.campaignId === step.campaignId);
  }
  return true;
};

/**
 * Removes ophaned steps
 * @param steps Journey steps to modify in place
 */
export const removeOrphans = (steps: JourneyStepAny[]): void => {
  const child = steps.filter(
    (s) => s.type !== JOURNEY_STEP_TYPE.SOURCE_EVENTS && s.type !== JOURNEY_STEP_TYPE.SOURCE_AUDIENCE
  );
  for (const step of child) {
    const prev = getStepsByNext(steps, step.id);
    if (!prev.length) {
      removeStep(steps, step);
    }
  }
};

/**
 * Clean interaction's actions if previous action step does not match
 * @param steps Journey steps to modify in place
 */
export const cleanInvalidInteractions = (steps: JourneyStepAny[]): void => {
  const child = steps.filter(
    (s) => s.type === JOURNEY_STEP_TYPE.FLOW_INTERACTION || s.type === JOURNEY_STEP_TYPE.FLOW_WAIT_INTERACTION
  ) as JourneyStepFlowInteraction[];
  for (const step of child) {
    const action = step.paths[0]?.action;
    const prevAction = getPreviousActions(steps, step.id)?.[0];
    if (action) {
      if (
        !prevAction ||
        (action === MESSAGE_EVENT.OPEN && prevAction.type === JOURNEY_STEP_TYPE.ACTION_MESSAGE_IN_APP) ||
        ((action === MESSAGE_EVENT.CLICK ||
          action === MESSAGE_EVENT.IMPRESSION ||
          action === MESSAGE_EVENT.DISMISSED_CLICK) &&
          (prevAction.type === JOURNEY_STEP_TYPE.ACTION_MESSAGE_EMAIL ||
            prevAction.type === JOURNEY_STEP_TYPE.ACTION_MESSAGE_PUSH))
      ) {
        step.paths = step.paths.map((s) => ({ ...s, action: '' }));
        delete step.mustEndInApp;
      }
    }
  }
};

/**
 * Counts the number of errors in the journey's steps
 * @param steps Journey steps
 */
export const countErrorsInJourney = (steps: JourneyStepAny[]): number => {
  // Detect invalid waits
  return needWaits(steps).length + hasContiguousWaits(steps).length;
};

/**
 * Counts the number of unfinished steps in the journey's steps
 * @param steps Journey steps
 */
export const countDraftsInJourney = (steps: JourneyStepAny[], messages?: MessagingMessage[]): number => {
  return steps.filter((s) => !isValidStep(s) || (messages && !isValidActionMessage(s, messages))).length;
};

/**
 * Read step's known duration. In case of multiple paths, returns the longest duration.
 * @param step Journey step
 * @param nextStep Next step, to get the duration of that path
 */
export const durationOfStep = (step: JourneyStepAny, nextStep?: JourneyStepAny): number => {
  if ('duration' in step && step.duration) {
    return step.duration;
  } else if (hasPaths(step)) {
    if (nextStep) {
      const path = step.paths.find((p) => p.next === nextStep.id);
      return path?.duration ?? 0;
    } else {
      // If next step is undefined, return the highest duration
      return Math.max(...step.paths.map((p) => p.duration ?? 0));
    }
  }
  return 0;
};

/**
 * Calculates journey paths' known duration, in seconds
 * @param steps Journey steps
 * @param endId End step to calculate the duration of the journey
 * @param beginId Optional first step of a journey, first step by default
 */
export const durationOfJourney = (
  steps: JourneyStepAny[],
  endId: JourneyStepId,
  beginId?: JourneyStepId
): number => {
  let duration = 0;
  const end = getStepById(steps, endId);
  if (!end) {
    return 0;
  }
  let prev: JourneyStepAny = end;
  do {
    const current = prev;
    prev = getStepsByNext(steps, prev.id)[0];
    if (prev) {
      if (beginId && prev.id === beginId) {
        // We have reached the beginning of the journey, return the duration
        return duration;
      } else {
        // Add the duration of the step
        duration += durationOfStep(prev, current);
      }
    }
  } while (prev);
  return duration;
};

/**
 * Calculates journey paths' known duration, in seconds
 * @param steps Journey steps
 */
export const durationsOfJourney = (steps: JourneyStepAny[]): number[] => {
  const ends = steps.filter((s) => s.type === JOURNEY_STEP_TYPE.END);
  return ends.map((e) => durationOfJourney(steps, e.id));
};

/**
 * Clones a journey changing every step Id
 * @param steps Journey to be cloned
 */
export const cloneJourney = (steps: JourneyStepAny[]): JourneyStepAny[] => {
  const clone = cloneDeep(steps);
  for (const step of clone) {
    changeStepId(clone, step.id);
  }
  return clone;
};

/**
 * Gets all the actions in a journey
 * @param steps Journey steps
 */
export const getActionsFromJourney = (steps: JourneyStepAny[]): JourneyStepAction[] =>
  steps.filter(isActionStep);

/**
 * Gets all the message actions in a journey
 * @param steps Journey steps
 */
export const getActionMessagesFromJourney = (steps: JourneyStepAny[]): JourneyStepActionMessage[] =>
  steps.filter(isActionMessageStep);

/**
 * Gets messageType of action message step
 * @param steps Journey steps
 */
export const getMessageTypeFromActionMessageStep = (step: JourneyStepActionMessage): MESSAGE_TYPE => {
  if (step.type === JOURNEY_STEP_TYPE.ACTION_MESSAGE_EMAIL) {
    return MESSAGE_TYPE.EMAIL;
  } else if (step.type === JOURNEY_STEP_TYPE.ACTION_MESSAGE_PUSH) {
    return MESSAGE_TYPE.PUSH;
  } else if (step.messageType) {
    return step.messageType;
  } else {
    console.error({ step });
    throw new Error('No messageType can be found');
  }
};

export const getAudienceFromJourney = (steps: JourneyStepAny[]): JourneyStepSourceAudience | undefined =>
  steps.find(isSourceAudienceStep);
